import React, { useRef, useEffect, useState, useId } from 'react';
import { Button } from './ui/button';
import { Eraser } from 'lucide-react';

function discardStroke(canvas, stroke) {
  const current = stroke.current;
  stroke.current = null;
  if (!current) return;
  const context = canvas.getContext('2d');
  if (current.snapshot) context.putImageData(current.snapshot, 0, 0);
  else context.clearRect(0, 0, canvas.width, canvas.height);
  if (canvas.hasPointerCapture?.(current.id)) canvas.releasePointerCapture(current.id);
}

export function SignaturePad({ value: suppliedValue, onChange, label = "Firma del cliente", disabled = false }) {
  const value = suppliedValue ?? '';
  const canvasRef = useRef(null), stroke = useRef(null), lastValue = useRef(null);
  const [empty, setEmpty] = useState(!value);
  const [restoring, setRestoring] = useState(Boolean(value));
  const [restoreFailed, setRestoreFailed] = useState(false);
  const id = useId();
  useEffect(() => { if (disabled) discardStroke(canvasRef.current, stroke); }, [disabled]);
  useEffect(() => {
    const canvas = canvasRef.current, ctx = canvas.getContext('2d');
    if (lastValue.current === value) return;
    let current = true;
    discardStroke(canvas, stroke);
    ctx.clearRect(0,0,canvas.width,canvas.height);
    lastValue.current = null;
    setEmpty(!value);
    setRestoring(Boolean(value));
    setRestoreFailed(false);
    if (value) {
      const img = new Image();
      img.onload = () => {
        if(current) {
          ctx.drawImage(img,0,0,canvas.width,canvas.height);
          lastValue.current = value;
          setRestoring(false);
        }
      };
      img.onerror = () => { if (current) { setRestoring(false); setRestoreFailed(true); } };
      img.src = value;
    } else lastValue.current = value;
    return () => { current = false; };
  },[value]);
  const position = e => {
    const c=canvasRef.current,r=c.getBoundingClientRect();
    return {x:(e.clientX-r.left)*c.width/r.width,y:(e.clientY-r.top)*c.height/r.height};
  };
  const start = e => {
    if(disabled || restoring || restoreFailed || lastValue.current !== value || stroke.current || (e.pointerType==='mouse'&&e.button!==0))return;
    e.preventDefault();const c=canvasRef.current,ctx=c.getContext('2d'),p=position(e);
    c.setPointerCapture(e.pointerId);ctx.lineWidth=2.2;ctx.lineCap='round';ctx.strokeStyle='#0f172a';ctx.beginPath();ctx.moveTo(p.x,p.y);
    stroke.current={id:e.pointerId,point:p,moved:false,snapshot:ctx.getImageData?.(0,0,c.width,c.height)};
  };
  const move = e => {
    const s=stroke.current;if(!s||s.id!==e.pointerId||disabled)return;
    const p=position(e);if(Math.hypot(p.x-s.point.x,p.y-s.point.y)<2)return;
    const ctx=canvasRef.current.getContext('2d');ctx.lineTo(p.x,p.y);ctx.stroke();s.moved=true;s.point=p;
  };
  const end = e => {
    const s=stroke.current;if(!s||s.id!==e.pointerId)return;
    if(disabled || e.type !== 'pointerup') {discardStroke(canvasRef.current,stroke);return;}
    stroke.current=null;
    if(canvasRef.current.hasPointerCapture?.(s.id))canvasRef.current.releasePointerCapture(s.id);
    if(s.moved){const data=canvasRef.current.toDataURL('image/png');lastValue.current=data;setEmpty(false);onChange(data);}
  };
  const clear = () => {if(disabled || restoring)return;discardStroke(canvasRef.current,stroke);canvasRef.current.getContext('2d').clearRect(0,0,520,180);lastValue.current='';setEmpty(true);setRestoreFailed(false);onChange('');};
  return <div><div className="mb-2 flex flex-wrap items-center justify-between gap-2"><span id={id} className="text-sm font-medium">{label}</span><Button type="button" variant="ghost" disabled={disabled || restoring} onClick={clear} data-testid="signature-clear"><Eraser className="mr-1 h-4 w-4" aria-hidden="true"/>Cancella firma</Button></div><div className="rounded-lg border border-border bg-white"><canvas ref={canvasRef} width={520} height={180} className="h-[180px] w-full touch-none rounded-lg" aria-labelledby={id} aria-describedby={`${id}-help`} aria-disabled={disabled || restoring || restoreFailed} data-testid="signature-canvas" onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerCancel={end} onLostPointerCapture={end}/></div><p id={`${id}-help`} className="mt-2 text-sm text-muted-foreground">{restoring?'Caricamento della firma…':restoreFailed?'Impossibile mostrare la firma salvata. Cancellala prima di firmare di nuovo.':empty?'Disegna la firma con mouse, penna o dito. Un solo tocco non registra una firma.':'Firma acquisita. Puoi cancellarla e ripetere.'}</p></div>;
}
